import Image from 'next/image'
import styled from 'styled-components'
import designRealRoomImg from '../../../assets/homepage-v2/design-real-room-card.png'
import discoverCardImg from '../../../assets/homepage-v2/discover-card.png'
import transformCardImg from '../../../assets/homepage-v2/transform-card.png'

const SectionWrapper = styled.section`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 80px 20px;

  @media (max-width: 768px) {
    padding: 40px 0;
  }
`

const SectionContent = styled.div`
  width: 1457px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;

  @media (max-width: 768px) {
    gap: 20px;
  }
`

const SectionTitle = styled.h2`
  color: black;
  font-size: 48px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  text-align: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 28px;
    padding: 0 20px;
  }
`

const SectionSubtitle = styled.p`
  color: #3f3f3f;
  font-size: 20px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  text-align: center;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 0 20px;
  }
`

const CardsContainer = styled.div`
  width: 100%;
  height: 605px;
  position: relative;
  display: flex;
  gap: 25px;
  justify-content: center;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    height: auto;
    justify-content: flex-start;
    overflow-x: auto;
    padding: 0 20px;
    gap: 10px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    &::after {
      content: '';
      flex-shrink: 0;
      width: 10px;
    }
  }
`

// Shared card styles
const Card = styled.div`
  width: 469px;
  height: 605px;
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 284px;
    height: 389px;
  }
`

const CardBg = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 25px;
`

const CardContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 29px 32px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`

const CardTitle = styled.h3`
  color: white;
  font-size: 32px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;
  margin: 0 0 10px 0;

  @media (max-width: 768px) {
    font-size: 20px;
    margin: 0 0 8px 0;
  }
`

const CardDescription = styled.p`
  color: white;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 400;
  margin: 0;
  max-width: 340px;

  @media (max-width: 768px) {
    font-size: 14px;
    max-width: 252px;
  }
`

const CardButton = styled.button`
  position: absolute;
  left: 64px;
  bottom: 26px;
  width: 340px;
  height: 44px;
  background: white;
  border: none;
  border-radius: 100px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  z-index: 2;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    left: 37px;
    bottom: 23px;
    width: 210px;
    height: 34px;
  }
`

const ButtonText = styled.span`
  color: black;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const CardImageArea = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 120px;
  bottom: 80px;
  z-index: 1;

  @media (max-width: 768px) {
    top: 90px;
    bottom: 60px;
  }
`

// Card 1: Design Your Real Room
const RealRoomBg = styled(CardBg)`
  background: linear-gradient(180deg, #e34d53 0%, #e06136 50%, #eeb089 100%);
`

// Card 2: AI Transform
const AITransformBg = styled(CardBg)`
  background: linear-gradient(180deg, #8000db 10%, #d530ff 34%, rgba(212, 73, 247, 0) 52%);
  z-index: 1;
`

const AICardImageArea = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`

const AIButton = styled(CardButton)`
  top: 196px;
  bottom: auto;

  @media (max-width: 768px) {
    top: 132px;
  }
`

const AIPromptInput = styled.div`
  position: absolute;
  left: 33px;
  top: 530px;
  width: 332px;
  height: 44px;
  background: white;
  border-radius: 10px;
  border: 1.5px solid #a438f6;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 4px;
  z-index: 2;

  @media (max-width: 768px) {
    left: 14px;
    top: 339px;
    width: 257px;
    height: 32px;
    padding: 0 12px;
  }
`

const SparkleText = styled.span`
  color: #a338f6;
  font-size: 16px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-style: italic;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const PromptText = styled.span`
  color: #545454;
  font-size: 16px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-style: italic;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

// Card 3: Discover
const DiscoverBg = styled(CardBg)`
  background: linear-gradient(180deg, #6f51ff 0%, #20ebe0 100%);
`

const DiscoverButton = styled(CardButton)`
  border-radius: 25px;
`

const FeaturesSection = ({ onActionClick }) => {
  return (
    <SectionWrapper>
      <SectionContent>
        <SectionTitle>Everything you need to design a home you love.</SectionTitle>
        <SectionSubtitle>
          Explore tools for real rooms, AI makeovers, inspiration, and interior design games.
        </SectionSubtitle>

        <CardsContainer>
          {/* Card 1: Design Your Real Room */}
          <Card>
            <RealRoomBg />
            <CardContent>
              <CardTitle>Design Your Real Room</CardTitle>
              <CardDescription>
                Upload your room and decorate with furniture, layouts, colors, and AR tools.
              </CardDescription>
            </CardContent>
            <CardImageArea>
              <Image
                src={designRealRoomImg}
                alt="Room design preview"
                layout="fill"
                objectFit="contain"
                objectPosition="center bottom"
              />
            </CardImageArea>
            <CardButton onClick={onActionClick}>
              <ButtonText>Start Designing</ButtonText>
            </CardButton>
          </Card>

          {/* Card 2: AI Transform */}
          <Card>
            <AICardImageArea>
              <Image
                src={transformCardImg}
                alt="AI transformed room"
                layout="fill"
                objectFit="cover"
                objectPosition="center bottom"
              />
            </AICardImageArea>
            <AITransformBg />
            <CardContent>
              <CardTitle>Transform Your Room with AI</CardTitle>
              <CardDescription>
                Generate room makeovers in seconds with AI styles, edits, and refinements.
              </CardDescription>
            </CardContent>
            <AIButton onClick={onActionClick}>
              <ButtonText>Explore AI Studio</ButtonText>
            </AIButton>
            <AIPromptInput>
              <SparkleText>.&#10022;&#11089;</SparkleText>
              <PromptText>Redesign my living room with bold colors</PromptText>
            </AIPromptInput>
          </Card>

          {/* Card 3: Discover */}
          <Card>
            <DiscoverBg />
            <CardContent>
              <CardTitle>Discover Interior Design Ideas</CardTitle>
              <CardDescription>
                Browse 21M+ community designs for inspiration, trends, and interior ideas.
              </CardDescription>
            </CardContent>
            <CardImageArea>
              <Image
                src={discoverCardImg}
                alt="Design ideas gallery"
                layout="fill"
                objectFit="contain"
                objectPosition="center bottom"
              />
            </CardImageArea>
            <DiscoverButton onClick={onActionClick}>
              <ButtonText>Explore Designs</ButtonText>
            </DiscoverButton>
          </Card>
        </CardsContainer>
      </SectionContent>
    </SectionWrapper>
  )
}

export default FeaturesSection
